// src/features/admin/pages/AdminAttendancePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LineChart, Line, ComposedChart, Cell,
} from "recharts";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

const GRAFANA_DAILY   = (import.meta.env.VITE_GRAFANA_DAILY_URL   || "").trim();
const GRAFANA_MONTHLY = (import.meta.env.VITE_GRAFANA_MONTHLY_URL || "").trim();

type DayItem   = { date: string;  count: number };
type MonthItem = { month: string; count: number };
type GrowthItem = { month: string; new_users: number; cumulative: number };
type Summary   = {
    today: number; this_week: number; this_month: number;
    total: number; total_users: number; new_users_this_month: number;
};
// TierItem / UsageTiers used in the commented-out 사용자 관여도 section
// type TierItem  = { label: string; seconds_min: number; count: number; pct: number };
// type UsageTiers = { month: string; total_active: number; tiers: TierItem[] };
type Retention = {
    month: string; prev_month: string;
    prev_users: number; curr_users: number;
    churned: number;  churn_rate: number;
    acquired: number; acquisition_rate: number;
    retained: number; retention_rate: number;
};

// TIER_COLORS and TIER_DESC are used in the commented-out 사용자 관여도 section below.
// Restore them when the heartbeat feature is activated.
// const TIER_COLORS = { Heavy: "#16a34a", Middle: "#2563eb", Light: "#d97706", Inactive: "#e4e4e7" };
// const TIER_DESC   = { Heavy: "월 30분 이상", Middle: "월 12~30분", Light: "월 12분 미만", Inactive: "이번 달 미사용" };

function monthOptions() {
    const opts: string[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return opts;
}

export default function AdminAttendancePage() {
    const [daily,      setDaily]      = useState<DayItem[]>([]);
    const [monthly,    setMonthly]    = useState<MonthItem[]>([]);
    const [userGrowth, setUserGrowth] = useState<GrowthItem[]>([]);
    const [summary,    setSummary]    = useState<Summary | null>(null);
    const [loading,    setLoading]    = useState(true);
    const [days,       setDays]       = useState(30);
    // Tiers state — restore when heartbeat is activated
    // const [usageTiers,   setUsageTiers]  = useState<UsageTiers | null>(null);
    // const [tiersLoading, setTiersLoading] = useState(true);
    // const [tierMonth,    setTierMonth]    = useState(() => new Date().toISOString().slice(0, 7));
    const [retention,     setRetention]     = useState<Retention | null>(null);
    const [retLoading,    setRetLoading]    = useState(true);
    const [retMonth,      setRetMonth]      = useState(() => new Date().toISOString().slice(0, 7));

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api(`/admin/attendance/daily?days=${days}`),
            api("/admin/attendance/monthly?months=12"),
            api("/admin/attendance/summary"),
            api("/admin/attendance/user-growth?months=12"),
        ]).then(([d, m, s, g]) => {
            setDaily(d.items || []);
            setMonthly(m.items || []);
            setSummary(s);
            setUserGrowth(g.items || []);
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, [days]);

    // Tiers fetch — restore when heartbeat is activated
    // useEffect(() => {
    //     setTiersLoading(true);
    //     api(`/admin/attendance/usage-tiers?month=${tierMonth}`)
    //         .then(setUsageTiers)
    //         .catch(console.error)
    //         .finally(() => setTiersLoading(false));
    // }, [tierMonth]);

    useEffect(() => {
        setRetLoading(true);
        api(`/admin/attendance/retention?month=${retMonth}`)
            .then(setRetention)
            .catch(console.error)
            .finally(() => setRetLoading(false));
    }, [retMonth]);

    // Derived metrics from daily data
    const activeDays = useMemo(() => daily.filter(r => r.count > 0), [daily]);
    const peakDau    = useMemo(() => Math.max(0, ...daily.map(r => r.count)), [daily]);
    const avgDau     = useMemo(() => {
        if (!activeDays.length) return 0;
        const sum = activeDays.reduce((a, r) => a + r.count, 0);
        return Math.round(sum / activeDays.length);
    }, [activeDays]);
    const dauMauRatio = useMemo(() => {
        if (!summary?.this_month || !summary?.today) return 0;
        return Math.round((summary.today / summary.this_month) * 100);
    }, [summary]);

    const downloadExcel = () => {
        const rows = [...daily].reverse().map(r => ({ 날짜: r.date, DAU: r.count }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = [{ wch: 14 }, { wch: 10 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "DAU");
        XLSX.writeFile(wb, `dau_${days}days_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const dailyData   = daily.map(r => ({ ...r, label: r.date.slice(5) }));
    const monthlyData = monthly.map(r => ({ ...r, label: r.month.slice(2) }));

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: FW.extraBold }}>DAU / MAU 통계</h2>
                <span style={{ fontSize: 13, fontWeight: FW.extraBold }}>
                    {new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "short" })}
                </span>
                <select
                    value={days}
                    onChange={e => setDays(Number(e.target.value))}
                    style={{ padding: "6px 10px", border: "1.5px solid #e4e4e7", borderRadius: RADIUS.sm, fontSize: 13, fontWeight: FW.medium, background: COLOR.white, cursor: "pointer" }}
                >
                    <option value={7}>최근 7일</option>
                    <option value={14}>최근 14일</option>
                    <option value={30}>최근 30일</option>
                    <option value={60}>최근 60일</option>
                    <option value={90}>최근 90일</option>
                </select>
            </div>

            {/* Primary DAU / WAU / MAU cards */}
            {summary && (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                        <StatCard label="DAU (오늘)" value={summary.today} color="#18181b" desc="오늘 접속한 순 사용자" />
                        <StatCard label="WAU (이번 주)" value={summary.this_week} color="#2563eb" desc="이번 주(월~오늘) 접속 순 사용자" />
                        <StatCard label="MAU (이번 달)" value={summary.this_month} color="#16a34a" desc="이번 달 접속 순 사용자" />
                    </div>

                    {/* Derived / engagement cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                        <StatCard
                            label="DAU/MAU 비율"
                            value={dauMauRatio}
                            unit="%"
                            color={dauMauRatio >= 20 ? "#16a34a" : dauMauRatio >= 10 ? "#d97706" : "#dc2626"}
                            desc="오늘 DAU ÷ 이번 달 MAU (스티키니스)"
                        />
                        <StatCard label={`Peak DAU (최근 ${days}일)`} value={peakDau} color="#7c3aed" desc="기간 내 최고 일별 활성 사용자" />
                        <StatCard label={`Avg DAU (최근 ${days}일)`} value={avgDau} color="#0891b2" desc="활성 일수 기준 평균 DAU" />
                    </div>

                    {/* User base cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                        <StatCard label="전체 누적 출석" value={summary.total} color="#71717a" desc="전체 출석 기록 수" />
                        <StatCard label="전체 회원 수" value={summary.total_users} color="#db2777" desc="활성·정지 포함 전체 가입자" />
                        <StatCard label="이번 달 신규 가입" value={summary.new_users_this_month} color="#d97706" desc="이번 달 신규 가입 회원" />
                    </div>
                </>
            )}

            {/* Grafana embeds */}
            {(GRAFANA_DAILY || GRAFANA_MONTHLY) && (
                <Section title="Grafana 대시보드">
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {GRAFANA_DAILY && (
                            <div>
                                <div style={subLabel}>일별 DAU (Grafana)</div>
                                <iframe src={GRAFANA_DAILY} style={{ width: "100%", height: 300, border: "none", borderRadius: 10 }} title="Daily Attendance" />
                            </div>
                        )}
                        {GRAFANA_MONTHLY && (
                            <div>
                                <div style={subLabel}>월별 MAU (Grafana)</div>
                                <iframe src={GRAFANA_MONTHLY} style={{ width: "100%", height: 300, border: "none", borderRadius: 10 }} title="Monthly Attendance" />
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* Daily DAU bar chart */}
            <Section
                title={`DAU 추이 (최근 ${days}일)`}
                action={
                    <button
                        onClick={downloadExcel}
                        disabled={loading || daily.length === 0}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            padding: "6px 12px", borderRadius: RADIUS.sm, border: "1.5px solid #e4e4e7",
                            background: COLOR.white, color: COLOR.textDark, fontSize: 12, fontWeight: FW.medium,
                            cursor: loading || daily.length === 0 ? "not-allowed" : "pointer",
                            opacity: loading || daily.length === 0 ? 0.5 : 1,
                        }}
                    >
                        ⬇ Excel 다운로드
                    </button>
                }
            >
                {loading ? <Skeleton h={220} /> : (
                    <ResponsiveContainer width="100%" height={days > 14 ? 260 : 220}>
                        <BarChart data={dailyData} margin={{ top: 4, right: 8, left: -16, bottom: days > 14 ? 40 : 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11, fill: "#71717a", ...(days > 14 ? { angle: -45, textAnchor: "end" } : {}) }}
                                tickLine={false}
                                axisLine={{ stroke: "#e4e4e7" }}
                                interval={0}
                            />
                            <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }}
                                cursor={{ fill: "#f4f4f5" }}
                                formatter={(v) => [v, "DAU"]}
                                labelFormatter={l => `날짜: ${l}`}
                            />
                            <Bar dataKey="count" fill="#18181b" radius={[4, 4, 0, 0]} maxBarSize={32} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Section>

            {/* MAU + User growth side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Section title="MAU 추이 (최근 12개월)">
                    {loading ? <Skeleton h={220} /> : (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={monthlyData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }}
                                    formatter={(v) => [v, "MAU"]}
                                    labelFormatter={l => `월: 20${l}`}
                                />
                                <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: "#2563eb", strokeWidth: 0 }} activeDot={{ r: 6 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </Section>

                <Section title="회원 수 추이 (최근 12개월)">
                    {loading ? <Skeleton h={220} /> : (
                        <ResponsiveContainer width="100%" height={220}>
                            <ComposedChart data={userGrowth.map(r => ({ ...r, label: r.month.slice(2) }))} margin={{ top: 4, right: 16, left: -16, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} allowDecimals={false} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }}
                                    labelFormatter={l => `월: 20${l}`}
                                />
                                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                <Bar yAxisId="left" dataKey="new_users" name="신규 가입" fill="#db2777" radius={[4, 4, 0, 0]} maxBarSize={28} opacity={0.85} />
                                <Line yAxisId="right" type="monotone" dataKey="cumulative" name="누적 회원 수" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 4, fill: "#7c3aed", strokeWidth: 0 }} activeDot={{ r: 6 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                </Section>
            </div>

            {/* Retention / churn / acquisition */}
            <Section
                title="이탈 / 유입 / 유지"
                action={
                    <select
                        value={retMonth}
                        onChange={e => setRetMonth(e.target.value)}
                        style={{ padding: "6px 10px", border: "1.5px solid #e4e4e7", borderRadius: RADIUS.sm, fontSize: 13, fontWeight: FW.medium, background: COLOR.white, cursor: "pointer" }}
                    >
                        {monthOptions().map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                }
            >
                {retLoading ? <Skeleton h={160} /> : !retention ? null : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {/* Context label */}
                        <div style={{ fontSize: 12, color: COLOR.textMuted }}>
                            비교: <strong>{retention.prev_month}</strong> ({(retention.prev_users ?? 0).toLocaleString()}명) → <strong>{retention.month}</strong> ({(retention.curr_users ?? 0).toLocaleString()}명)
                        </div>

                        {/* 3-column cards */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                            {/* Churned */}
                            <div style={{ background: COLOR.bgLight, borderRadius: RADIUS.sm, padding: "14px 16px", borderLeft: "4px solid #dc2626" }}>
                                <div style={{ fontSize: 11, fontWeight: FW.bold, color: COLOR.textMuted, marginBottom: 4 }}>이탈 고객</div>
                                <div style={{ fontSize: 24, fontWeight: FW.extraBold, color: "#dc2626" }}>
                                    {(retention.churned ?? 0).toLocaleString()}<span style={{ fontSize: 13, marginLeft: 3 }}>명</span>
                                </div>
                                <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>
                                    이탈률 {retention.churn_rate}%
                                    <span style={{ marginLeft: 6, color: "#a1a1aa" }}>전월 대비</span>
                                </div>
                            </div>

                            {/* Acquired */}
                            <div style={{ background: COLOR.bgLight, borderRadius: RADIUS.sm, padding: "14px 16px", borderLeft: "4px solid #16a34a" }}>
                                <div style={{ fontSize: 11, fontWeight: FW.bold, color: COLOR.textMuted, marginBottom: 4 }}>유입 고객</div>
                                <div style={{ fontSize: 24, fontWeight: FW.extraBold, color: "#16a34a" }}>
                                    {(retention.acquired ?? 0).toLocaleString()}<span style={{ fontSize: 13, marginLeft: 3 }}>명</span>
                                </div>
                                <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>
                                    유입률 {retention.acquisition_rate}%
                                    <span style={{ marginLeft: 6, color: "#a1a1aa" }}>당월 대비</span>
                                </div>
                            </div>

                            {/* Retained */}
                            <div style={{ background: COLOR.bgLight, borderRadius: RADIUS.sm, padding: "14px 16px", borderLeft: "4px solid #2563eb" }}>
                                <div style={{ fontSize: 11, fontWeight: FW.bold, color: COLOR.textMuted, marginBottom: 4 }}>유지 고객</div>
                                <div style={{ fontSize: 24, fontWeight: FW.extraBold, color: "#2563eb" }}>
                                    {(retention.retained ?? 0).toLocaleString()}<span style={{ fontSize: 13, marginLeft: 3 }}>명</span>
                                </div>
                                <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>
                                    유지율 {retention.retention_rate}%
                                    <span style={{ marginLeft: 6, color: "#a1a1aa" }}>당월 대비</span>
                                </div>
                            </div>

                        </div>

                    </div>
                )}
            </Section>

            {/* Usage tiers — hidden until heartbeat is activated
            <Section
                title="사용자 관여도"
                action={
                    <select
                        value={tierMonth}
                        onChange={e => setTierMonth(e.target.value)}
                        style={{ padding: "6px 10px", border: "1.5px solid #e4e4e7", borderRadius: RADIUS.sm, fontSize: 13, fontWeight: FW.medium, background: COLOR.white, cursor: "pointer" }}
                    >
                        {monthOptions().map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                }
            >
                {tiersLoading ? <Skeleton h={160} /> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                            {(usageTiers?.tiers ?? []).map(t => (
                                <div key={t.label} style={{ background: COLOR.bgLight, borderRadius: RADIUS.sm, padding: "14px 16px", borderLeft: `4px solid ${TIER_COLORS[t.label]}` }}>
                                    <div style={{ fontSize: 11, fontWeight: FW.bold, color: COLOR.textMuted, marginBottom: 4 }}>{t.label.toUpperCase()}</div>
                                    <div style={{ fontSize: 24, fontWeight: FW.extraBold, color: TIER_COLORS[t.label] }}>
                                        {t.count.toLocaleString()}<span style={{ fontSize: 13, marginLeft: 3 }}>명</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>{t.pct}% · {TIER_DESC[t.label]}</div>
                                </div>
                            ))}
                        </div>
                        {usageTiers && usageTiers.tiers.some(t => t.count > 0) ? (
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={usageTiers.tiers} margin={{ top: 4, right: 8, left: -16, bottom: 4 }} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} allowDecimals={false} />
                                    <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: "#71717a" }} tickLine={false} axisLine={false} width={60} />
                                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} cursor={{ fill: "#f4f4f5" }} formatter={(v: number, _: string, props: any) => [`${v.toLocaleString()}명 (${props.payload.pct}%)`, props.payload.label]} />
                                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={32}>
                                        {usageTiers.tiers.map(t => (<Cell key={t.label} fill={TIER_COLORS[t.label]} />))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ textAlign: "center", color: COLOR.textLight, fontSize: 13, padding: "20px 0" }}>
                                데이터 없음 — 하트비트 기능 활성화 후 데이터가 쌓입니다
                            </div>
                        )}
                    </div>
                )}
            </Section>
            */}

            {/* Raw table */}
            <Section title="일별 DAU 상세">
                {loading ? <Skeleton h={160} /> : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: "#f4f4f5" }}>
                                    <th style={th}>날짜</th>
                                    <th style={th}>DAU</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...daily].reverse().filter(r => r.count > 0).map(r => (
                                    <tr key={r.date} style={{ borderBottom: "1px solid #f4f4f5" }}>
                                        <td style={td}>{r.date}</td>
                                        <td style={{ ...td, fontWeight: FW.bold }}>{r.count.toLocaleString()}</td>
                                    </tr>
                                ))}
                                {activeDays.length === 0 && (
                                    <tr><td colSpan={2} style={{ ...td, color: COLOR.textLight, textAlign: "center" }}>데이터 없음</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>
        </div>
    );
}

function StatCard({ label, value, color, unit = "명", desc }: { label: string; value: number; color: string; unit?: string; desc?: string }) {
    return (
        <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, padding: "18px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: FW.bold, color: COLOR.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: FW.extraBold, color }}>
                {value.toLocaleString()}<span style={{ fontSize: 14, fontWeight: FW.medium, marginLeft: 3, color }}>{unit}</span>
            </div>
            {desc && <div style={{ marginTop: 6, fontSize: 11, color: COLOR.textLight }}>{desc}</div>}
        </div>
    );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div style={{ background: COLOR.white, border: BORDER.lightAlt, borderRadius: RADIUS.md, padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: FW.bold, color: COLOR.textDark }}>{title}</div>
                {action}
            </div>
            {children}
        </div>
    );
}

function Skeleton({ h }: { h: number }) {
    return <div style={{ height: h, background: COLOR.bgLight, borderRadius: RADIUS.sm }} />;
}

const subLabel: React.CSSProperties = { fontSize: 12, fontWeight: FW.medium, color: COLOR.textMuted, marginBottom: 8 };
const th: React.CSSProperties = { padding: "8px 12px", textAlign: "left", fontWeight: FW.bold, color: "#3f3f46" };
const td: React.CSSProperties = { padding: "8px 12px", color: COLOR.textDark };